import type { NhlTiebreakerTeam, TiebreakerStepFn, StepContext } from '../../shared/tiebreaker/types';
import { pointsPctStep, regulationWinsStep, rowStep, totalWinsStep } from '../../shared/tiebreaker/steps';
import { calculateH2hScoringRecords, createH2hScoringStep } from '../../shared/tiebreaker/steps/h2h-scoring';
import { createGoalDifferentialStep } from '../../shared/tiebreaker/steps/nhl-goal-differential';
import { createGoalsForStep } from '../../shared/tiebreaker/steps/nhl-goals-for';
import { coinFlipStep } from '../../shared/tiebreaker/steps/coin-flip';

export interface NhlCriterion {
  step: TiebreakerStepFn<NhlTiebreakerTeam>;
  label?: string;
  score: (
    teams: NhlTiebreakerTeam[],
    ctx: StepContext<NhlTiebreakerTeam>,
  ) => (team: NhlTiebreakerTeam) => number;
}

// Keep each scoring function beside its step so every score bucket from the
// original tied group can be ordered before advancing still-tied buckets.
export const NHL_CONF_CRITERIA: NhlCriterion[] = [
  { step: pointsPctStep, label: 'Points%', score: () => team => team.pointsPct },
  { step: regulationWinsStep, label: 'RegWins', score: () => team => team.regulationWins },
  { step: rowStep, label: 'ROW', score: () => team => team.row },
  { step: totalWinsStep, label: 'TotalWins', score: () => team => team.totalWins },
  {
    step: createH2hScoringStep<NhlTiebreakerTeam>(),
    score: (teams, ctx) => {
      const { records } = calculateH2hScoringRecords(teams, ctx.games);
      return team => records.get(team.teamAbbreviation)?.percentage ?? -1;
    },
  },
  { step: createGoalDifferentialStep(), score: () => team => team.goalsFor - team.goalsAgainst },
  { step: createGoalsForStep(), score: () => team => team.goalsFor },
];

export const NHL_CONF_STEPS: TiebreakerStepFn<NhlTiebreakerTeam>[] = [
  ...NHL_CONF_CRITERIA.map(criterion => criterion.step),
  coinFlipStep as TiebreakerStepFn<NhlTiebreakerTeam>,
];
